import { Button, Card, Form, Input, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import { getApplicationSecretKey } from "./ApplicationPage";
import ApplicationPermissionSelector from "./components/ApplicationPermissionSelector";
import type { ApplicationFormValues, ApplicationRow } from "./types";

export interface ApplicationFormPageProps {
    getSecretKey?: (id: number | string) => Promise<string>;
    loadApplication?: (id: number | string) => Promise<ApplicationRow>;
    saveApplication?: (id: number | string | undefined, values: ApplicationFormValues) => Promise<void>;
}

/** 复用旧 Vue `/application/:id` 详情接口。 */
export async function loadApplication(id: number | string): Promise<ApplicationRow> {
    const response = await request.service.get<ApplicationRow>(`/application/${id}`);
    return response.data;
}

/** 复用旧 Vue 新增/编辑接口，新增使用 POST，编辑使用 PUT。 */
export async function saveApplication(id: number | string | undefined, values: ApplicationFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/application", values);
        return;
    }
    await request.service.put(`/application/${id}`, values);
}

interface ApplicationFormState {
    appName: string;
    callbackAddress?: string;
    applications: string[];
}

/** 应用新增/编辑页，完整复用 Vue 表单字段和接口契约。 */
export function ApplicationFormPage({
    getSecretKey = getApplicationSecretKey,
    loadApplication: load = loadApplication,
    saveApplication: save = saveApplication
}: ApplicationFormPageProps) {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    // 沿用 Vue 表单的全局封禁态，仅限制保存，不影响字段读取和返回导航。
    const banButton = useUserStore(state => state.banButton);
    const [form] = Form.useForm<ApplicationFormState>();
    const [application, setApplication] = useState<ApplicationRow | null>(null);
    const [secretKey, setSecretKey] = useState<string | null>(null);
    const [secretKeyVisible, setSecretKeyVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void load(id)
            .then(info => {
                if (!disposed) {
                    setApplication(info);
                    form.setFieldsValue({
                        appName: info.appName || "",
                        callbackAddress: info.callbackAddress,
                        applications: info.applications || []
                    });
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("应用信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [form, id, load]);

    // Vue 表单只在显式点击时请求明文 SK，重复点击可恢复为已加载的默认值。
    const handleToggleSecretKey = async () => {
        if (!id) return;
        if (secretKeyVisible) {
            setSecretKeyVisible(false);
            return;
        }
        try {
            setSecretKey(await getSecretKey(id));
            setSecretKeyVisible(true);
        } catch {
            setErrorMessage("应用 SK 获取失败");
        }
    };

    const handleFinish = async (values: ApplicationFormState) => {
        const applications = values.applications || [];
        if (applications.length === 0) {
            setErrorMessage("请选择功能点");
            return;
        }
        try {
            await save(id, {
                appName: values.appName,
                callbackAddress: values.callbackAddress,
                applications
            });
            void navigate(-1);
        } catch {
            // 保存接口失败时保留当前表单，并反馈与 Vue 页面一致的错误状态。
            setErrorMessage("应用保存失败");
        }
    };

    return (
        <section className="detail-page-transparent flex h-full min-h-full flex-col" aria-label="application-form-page">
            <Card className="flex-1">
                <Typography.Title className="mb-6 mt-0" level={4}>
                    {id ? "编辑应用" : "新增应用"}
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Form<ApplicationFormState>
                    form={form}
                    labelAlign="right"
                    labelCol={{ flex: "120px" }}
                    wrapperCol={{ flex: 1 }}
                    onFinish={values => void handleFinish(values)}
                >
                    <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-2">
                        <Form.Item
                            label="应用名称"
                            name="appName"
                            rules={[{ required: true, message: "请输入应用名称" }]}
                        >
                            <Input allowClear maxLength={25} placeholder="请输入应用名称" />
                        </Form.Item>
                        {id ? (
                            <>
                                <Form.Item label="AK (access key)">
                                    <Typography.Text className="break-all">
                                        {application?.accessKey || DEFAULT_VALUE}
                                    </Typography.Text>
                                </Form.Item>
                                <Form.Item label="SK (secret key)">
                                    <Space>
                                        <Typography.Text className="break-all">
                                            {(secretKeyVisible ? secretKey : application?.secretKey) || DEFAULT_VALUE}
                                        </Typography.Text>
                                        <Button
                                            htmlType="button"
                                            onClick={() => void handleToggleSecretKey()}
                                            type="link"
                                        >
                                            {secretKeyVisible ? "隐藏 SK" : "查看 SK"}
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </>
                        ) : null}
                        <Form.Item label="回调地址" name="callbackAddress">
                            <Input allowClear maxLength={200} placeholder="请输入回调地址" showCount />
                        </Form.Item>
                    </div>
                    <div className="detail-json__section-title mb-4 mt-4">功能点</div>
                    <Form.Item
                        label=""
                        name="applications"
                        wrapperCol={{ flex: 1 }}
                        rules={[
                            {
                                validator: (_, value: string[]) =>
                                    value?.length ? Promise.resolve() : Promise.reject(new Error("请选择功能点"))
                            }
                        ]}
                    >
                        <ApplicationPermissionSelector />
                    </Form.Item>
                </Form>
            </Card>
            <Space className="operation-fixed-btn" size={0}>
                <Button disabled={banButton} htmlType="submit" type="primary" onClick={() => void form.submit()}>
                    保存
                </Button>
                <Button type="primary" onClick={() => void navigate(-1)}>
                    返回
                </Button>
            </Space>
        </section>
    );
}

export default ApplicationFormPage;
